"use client"

import { BarsThree, MagnifyingGlass, User } from "@medusajs/icons"
import { usePathname } from "next/navigation"
import type { ReactNode } from "react"
import LocalizedClientLink from "@modules/common/components/localized-client-link"
import CartButton from "@modules/layout/components/cart-button"
import type { PublicStorefrontSettings, ResolvedMenuItem } from "../../types"
import { useGlobalAside } from "../../context/global-aside-context"
import MobileNavigationAside from "../mobile-navigation-aside"
import SearchAside from "../search-aside"
import { useScrollHeader } from "./use-scroll-header"
import styles from "./header.module.css"

function isActivePath(pathname: string, href: string) {
  if (href === "/") return pathname === "/" || /^\/[a-z]{2}\/?$/i.test(pathname)
  return pathname === href || pathname.endsWith(href)
}

function HeaderMenuLink({ item, pathname }: { item: ResolvedMenuItem; pathname: string }) {
  const className = `${styles.menuLink} ${!item.external && isActivePath(pathname, item.href) ? styles.menuLinkActive : ""}`

  if (item.external) {
    return (
      <a
        className={className}
        href={item.href}
        target={item.new_tab ? "_blank" : undefined}
        rel="noopener noreferrer"
      >
        {item.label}
      </a>
    )
  }

  return (
    <LocalizedClientLink
      className={className}
      href={item.href}
      target={item.new_tab ? "_blank" : undefined}
      rel={item.new_tab ? "noopener noreferrer" : undefined}
    >
      {item.label}
    </LocalizedClientLink>
  )
}

function Logo({ settings }: { settings: PublicStorefrontSettings }): ReactNode {
  return settings.store.logo ? (
    <img
      className={styles.logoImage}
      src={settings.store.logo.url}
      alt={settings.store.logo.alt || settings.store.name}
    />
  ) : (
    <span className={styles.wordmark}>{settings.store.name}</span>
  )
}

export function HeaderChrome({
  settings,
  pathname,
}: {
  settings: PublicStorefrontSettings
  pathname: string
}) {
  const { open } = useGlobalAside()

  return (
    <header className={styles.header}>
      <div className={styles.inner}>
        <div className={styles.desktopNav}>
          <nav aria-label="Ana menü" className={styles.menu}>
            {settings.header.items.map((item) => (
              <HeaderMenuLink key={item.id} item={item} pathname={pathname} />
            ))}
          </nav>
        </div>

        <div className={styles.mobileLead}>
          <button
            type="button"
            className={styles.actionButton}
            aria-label="Menüyü aç"
            onClick={(event) => open("mobile", event.currentTarget)}
          >
            <BarsThree aria-hidden="true" />
          </button>
        </div>

        <LocalizedClientLink href="/" className={styles.logoLink} aria-label={`${settings.store.name} ana sayfa`}>
          <Logo settings={settings} />
        </LocalizedClientLink>

        <div className={styles.actions}>
          <LocalizedClientLink className={`${styles.actionButton} ${styles.desktopAccount}`} href="/account" aria-label="Hesabım">
            <User aria-hidden="true" />
          </LocalizedClientLink>
          <button
            type="button"
            className={styles.actionButton}
            aria-label="Aramayı aç"
            onClick={(event) => open("search", event.currentTarget)}
          >
            <MagnifyingGlass aria-hidden="true" />
          </button>
          <CartButton />
        </div>
      </div>
    </header>
  )
}

export default function Header({ settings }: { settings: PublicStorefrontSettings }) {
  const pathname = usePathname()
  const { passed, visible, sentinelRef } = useScrollHeader()

  return (
    <>
      <div ref={sentinelRef} className={styles.sentinel} aria-hidden="true" />
      <div className={styles.wrapper} data-testid="header-flow">
        <HeaderChrome settings={settings} pathname={pathname} />
      </div>
      {passed ? (
        <div
          className={`${styles.wrapper} ${styles.fixed} ${visible ? styles.fixedVisible : ""}`}
          data-testid="header-fixed"
          aria-hidden={!visible}
          inert={!visible ? true : undefined}
        >
          <HeaderChrome settings={settings} pathname={pathname} />
        </div>
      ) : null}
      <MobileNavigationAside settings={settings} />
      <SearchAside />
    </>
  )
}
